import { InviteEoaUserRole, InviteUserRole, UserListItem } from '../../interfaces/UserManagementInterface';
import { EOA_INVITE_ROLES, USER_ROLES, USER_ROLES_LABELS } from '../../constants';

type UserStatusTagType = 'pending' | 'completed' | 'error';

interface UserStatusDisplay {
  label: string;
  tagType: UserStatusTagType;
}

interface UserRowActions {
  canResendInvite: boolean;
  canResetPassword: boolean;
  canResetMfa: boolean;
  canDeactivate: boolean;
  canActivate: boolean;
}

interface RolePayloads {
  platform_roles: InviteUserRole[];
  eoa_roles: InviteEoaUserRole[];
}

const EOA_ROLE_SET = new Set<string>(EOA_INVITE_ROLES);

const getUserStatusDisplay = (user: UserListItem): UserStatusDisplay => {
  if (user.status === 'INACTIVE') {
    return { label: 'Inactive', tagType: 'error' };
  }

  if (user.can_resend_invite) {
    return { label: 'Pending invite', tagType: 'pending' };
  }

  return { label: 'Active', tagType: 'completed' };
};

const getUserRowActions = (user: UserListItem): UserRowActions => ({
  canResendInvite: user.can_resend_invite,
  canResetPassword: user.is_auth0_linked && user.status === 'ACTIVE',
  canResetMfa: user.is_auth0_linked && user.status === 'ACTIVE',
  canDeactivate: user.status === 'ACTIVE',
  canActivate: user.status === 'INACTIVE',
});

const formatRoleLabel = (roleName: string): string => USER_ROLES_LABELS[roleName] ?? roleName;

const requiresCohortSelection = (selectedRoles: string[], cohortEoaIds: string[]): boolean =>
  selectedRoles.some((role) => EOA_ROLE_SET.has(role)) && cohortEoaIds.length === 0;

// Each EOA role is assigned within every selected cohort; the backend derives the
// user's organization from the cohort, so no organization_id is sent from the client.
const buildRolePayloads = (selectedRoles: string[], cohortEoaIds: string[]): RolePayloads => {
  const eoaRoles = selectedRoles.filter((role) => EOA_ROLE_SET.has(role));
  const platformRoles = selectedRoles.filter((role) => !EOA_ROLE_SET.has(role));
  return {
    platform_roles: platformRoles.map((name) => ({ name })),
    eoa_roles: cohortEoaIds.flatMap((eoaId) => eoaRoles.map((name) => ({ name, eoa_id: eoaId }))),
  };
};

// Non-super-admins may view but not change the Super Admin role: force it back to its
// previous state on every edit (also enforced on the backend).
const applyRoleSelection = (nextRoles: string[], currentRoles: string[], canManageSuperAdmin: boolean): string[] => {
  if (canManageSuperAdmin) return nextRoles;
  const locked = nextRoles.filter((role) => role !== USER_ROLES.SUPER_ADMIN);
  if (currentRoles.includes(USER_ROLES.SUPER_ADMIN)) locked.push(USER_ROLES.SUPER_ADMIN);
  return locked;
};

const getErrorDetail = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

export type { UserStatusDisplay, UserRowActions, RolePayloads };
export {
  getUserStatusDisplay,
  getUserRowActions,
  formatRoleLabel,
  requiresCohortSelection,
  buildRolePayloads,
  applyRoleSelection,
  getErrorDetail,
};
